import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle.min';
import SignatureModal from './SignatureModal/SignatureModal';
import VitalSignsModal from './VitalSignsModal';
import axiosInstance from '../../../../../api';

// PatientSituationtransfercaseModal component for handling patient transfer case details
const PatientSituationtransfercaseModal = ({ isOpen, onClose, onSave }) => {
  // State to manage form data, aligned with API payload
  const [formData, setFormData] = useState({
    case_id: '',
    user_id: '',
    filled_by: '',
    transport_mode: { ambulance: 0, medivac: 0 },
    physician_agreement: 0,
    monitoring_type: '',
    monitoring_interval: '',
    monitoring_unit: '',
    transferring_physician: '',
    transferring_signature: '',
    accepting_physician: '',
    contact_no: '',
    vital_signs: [{
      BP: '',
      P: '',
      RR: '',
      SPO2: '',
      Temp: '',
      time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    }],
    reason_for_transfer: [{ Consultation: 0, Discharge: 0, SpecificManagement: 0, transferofcare: 0 }],
    patient_status: 0,
    respiratory_support_type: { roomair: 0, incubator: 0, ventilated: 0, o2therapy: 0, 'l/m': 0 },
    urgency_of_transfer: { emergent: 0, urgent: 0, semiurgent: 0, nonurgent: 0 },
    medication: [{ medication_name: '', dose: '', route: '', time: '' }],
    diagnosis: '',
    clinical_summary: '',
    past_medical_history_pmhx: {
      Cardiac: 0,
      Stroke: 0,
      Asthma: 0,
      Allergy: 0,
      HTN: 0,
      COPD: 0,
      DM: 0,
      None: 0,
      Other: 0,
      Other_value: '',
    },
    infectious_disease: 0,
    precautions: { Airborne: 0, Droplet: 0, Contact: 0, Bloodborne: 0, Other: 0, Other_value: '' },
    is_medication_during_transfer: 0,
    medication_during_transfer: [{ medication_name: '', dose: '' }],
    patient_requirement_during_transfer: '',
    is_applicable_ventilator_setting: 0,
    ventilator_setting: { tv: '', peep: '', o2: '', rate: '' },
    is_applicable_fluid_therapy: 0,
    fluid_therapy: [
      { name: 'Ns', is_active: 0, value: '' },
      { name: 'Rl', is_active: 0, value: '' },
      { name: 'd545', is_active: 0, value: '' },
      { name: 'other', is_active: 0, value: '' },
    ],
    physical_recomondation: '',
    transfer_summary: '',
    vital_sign_during_transfer: { bp: '', p: '', rr: '', spo2: '', temp: '' },
    recieved_party_signatures: {
      received_dr_name: '',
      dr_signature: '',
      received_nurse_name: '',
      nurse_signature: '',
      received_paramedic_name: sessionStorage.getItem('Role_Name') === 'EMS Team' ? sessionStorage.getItem('User_Name') || '' : '',
      paramedic_signature: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().split(' ')[0],
    },
    infusion_pump: '',
  });

  const [signatureModalType, setSignatureModalType] = useState(null);
  const [currentSignatureField, setCurrentSignatureField] = useState(null);
  const [vitalModalOpen, setVitalModalOpen] = useState(false);
  const [vitalSignCount, setVitalSignCount] = useState(1);
  const [isFormEditable, setIsFormEditable] = useState(true);
  const CaseIdTransferCase = sessionStorage.getItem('currentCaseId');
  const RoleName = sessionStorage.getItem('Role_Name');
  const userId = sessionStorage.getItem('user_id') || 0;
  const patientModalRef = useRef(null);
  const defaultVitalSigns = ['BP', 'P', 'RR', 'SPO2', 'Temp', 'time'];
  const [isLoading, setIsLoading] = useState(false);
  const [editCheckLoading, setEditCheckLoading] = useState(true);
  const [isEditableByAPI, setIsEditableByAPI] = useState(false);

  // Check if EMS Team can edit the form
  useEffect(() => {
    const checkEditAvailability = async () => {
      if (RoleName === 'EMS Team' && CaseIdTransferCase) {
        try {
          const response = await axiosInstance.get(`/ems/is-available-for-edit/${CaseIdTransferCase}`, {
            headers: {
              'Accept': 'application/vnd.api+json',
              'Content-Type': 'application/vnd.api+json',
              'Authorization': `Bearer ${sessionStorage.getItem('authToken')}`
            }
          });
          
          // If status is true, allow editing; if false, disable editing
          setIsEditableByAPI(response.data.status === true);
        } catch (error) {
          console.error('Error checking edit availability:', error);
          // In case of error, disable editing for safety
          setIsEditableByAPI(false);
        } finally {
          setEditCheckLoading(false);
        }
      } else {
        // For non-EMS Team roles, maintain original logic
        setIsEditableByAPI(RoleName === 'EMS Team');
        setEditCheckLoading(false);
      }
    };

    checkEditAvailability();
  }, [CaseIdTransferCase, RoleName]);

  // Role-based editability logic
  const isEditable = (section = 'any') => {
    if (RoleName === 'EMS Team') {
      return isEditableByAPI;
    }

    if (!['Hospital Representative (Nurse)', 'Nurse', 'EMS Team'].includes(RoleName)) return false;

    // Nurse roles can only view — no editing allowed
    if (RoleName === 'Hospital Representative (Nurse)' || RoleName === 'Nurse') {
      return false; // All fields read-only for Nurse
    }

    return true;
  };

  const requiresValidation = () => {
    return !['Hospital Representative (Nurse)', 'Nurse'].includes(RoleName);
  };

  // Initialize and manage Bootstrap modal
  useEffect(() => {
    const modalElement = document.getElementById('bs-fullscreen-transfer');
    if (!modalElement) {
      console.warn('Modal element not found');
      return;
    }

    let modalInstance = null;
    try {
      modalInstance = new bootstrap.Modal(modalElement, { backdrop: 'static', keyboard: false });
      patientModalRef.current = modalInstance;
    } catch (error) {
      console.warn('Error initializing modal:', error.message);
      return;
    }

    if (isOpen && modalInstance && modalElement.isConnected) {
      try {
        modalInstance.show();
      } catch (error) {
        console.warn('Error showing modal:', error.message);
      }
    } else if (patientModalRef.current && modalElement.isConnected) {
      try {
        modalInstance.hide();
      } catch (error) {
        console.warn('Error hiding modal:', error.message);
      }
    }

    return () => {
      if (patientModalRef.current && modalElement && modalElement.isConnected) {
        try {
          patientModalRef.current.hide();
          patientModalRef.current.dispose();
        } catch (error) {
          console.warn('Error during modal cleanup:', error.message);
        }
        patientModalRef.current = null;
      }
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop && backdrop.isConnected) {
        backdrop.remove();
      }
      if (document.body.classList.contains('modal-open')) {
        document.body.classList.remove('modal-open');
      }
    };
  }, [isOpen]);

  const handleClose = () => {
    if (patientModalRef.current) patientModalRef.current.hide();
    onClose();
  };

  // Fetch transfer case data from API and set editability
  useEffect(() => {
    const fetchTransferCase = async () => {
      if (!CaseIdTransferCase) return;

      try {
        const response = await axiosInstance.get(
          `/ems/get-transfer-case-patient-situation/${CaseIdTransferCase}`,
          {
            headers: {
              Accept: 'application/vnd.api+json',
              'Content-Type': 'application/vnd.api+json',
              Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
            },
          }
        );

        const data = response.data[0] || {};
        setFormData((prev) => ({
          ...prev,
          case_id: CaseIdTransferCase,
          user_id: userId,
          filled_by: RoleName || '',
          transport_mode: {
            ambulance: data.transport_mode?.ambulance || 0,
            medivac: data.transport_mode?.medivac || 0,
          },
          physician_agreement: data.physician_agreement || 0,
          monitoring_type: data.monitoring_type || '',
          monitoring_interval: data.monitoring_interval || '',
          monitoring_unit: data.monitoring_unit || '',
          transferring_physician: data.transferring_physician || '',
          transferring_signature: data.transferring_signature || '',
          accepting_physician: data.accepting_physician || '',
          contact_no: data.contact_no || '',
          vital_signs: data.vital_signs?.length
            ? data.vital_signs
            : [{
                BP: '',
                P: '',
                RR: '',
                SPO2: '',
                Temp: '',
                time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
              }],
          reason_for_transfer: data.reason_for_transfer?.length
            ? data.reason_for_transfer
            : [{ Consultation: 0, Discharge: 0, SpecificManagement: 0, transferofcare: 0 }],
          patient_status: data.patient_status || 0,
          respiratory_support_type: {
            roomair: data.respiratory_support_type?.roomair || 0,
            incubator: data.respiratory_support_type?.incubator || 0,
            ventilated: data.respiratory_support_type?.ventilated || 0,
            o2therapy: data.respiratory_support_type?.o2therapy || 0,
            'l/m': data.respiratory_support_type?.['l/m'] || 0,
          },
          urgency_of_transfer: {
            emergent: data.urgency_of_transfer?.emergent || 0,
            urgent: data.urgency_of_transfer?.urgent || 0,
            semiurgent: data.urgency_of_transfer?.semiurgent || 0,
            nonurgent: data.urgency_of_transfer?.nonurgent || 0,
          },
          medication: data.medication?.length
            ? data.medication
            : [{ medication_name: '', dose: '', route: '', time: '' }],
          diagnosis: data.diagnosis || '',
          clinical_summary: data.clinical_summary || '',
          past_medical_history_pmhx: {
            Cardiac: data.past_medical_history_pmhx?.Cardiac || 0,
            Stroke: data.past_medical_history_pmhx?.Stroke || 0,
            Asthma: data.past_medical_history_pmhx?.Asthma || 0,
            Allergy: data.past_medical_history_pmhx?.Allergy || 0,
            HTN: data.past_medical_history_pmhx?.HTN || 0,
            COPD: data.past_medical_history_pmhx?.COPD || 0,
            DM: data.past_medical_history_pmhx?.DM || 0,
            None: data.past_medical_history_pmhx?.None || 0,
            Other: data.past_medical_history_pmhx?.Other || 0,
            Other_value: data.past_medical_history_pmhx?.Other_value || '',
          },
          infectious_disease: data.infectious_disease || 0,
          precautions: {
            Airborne: data.precautions?.Airborne || 0,
            Droplet: data.precautions?.Droplet || 0,
            Contact: data.precautions?.Contact || 0,
            Bloodborne: data.precautions?.Bloodborne || 0,
            Other: data.precautions?.Other || 0,
            Other_value: data.precautions?.Other_value || '',
          },
          is_medication_during_transfer: data.is_medication_during_transfer || 0,
          medication_during_transfer: data.medication_during_transfer?.length
            ? data.medication_during_transfer
            : [{ medication_name: '', dose: '' }],
          patient_requirement_during_transfer: data.patient_requirement_during_transfer || '',
          is_applicable_ventilator_setting: data.is_applicable_ventilator_setting || 0,
          ventilator_setting: {
            tv: data.ventilator_setting?.tv || '',
            peep: data.ventilator_setting?.peep || '',
            o2: data.ventilator_setting?.o2 || '',
            rate: data.ventilator_setting?.rate || '',
          },
          is_applicable_fluid_therapy: data.is_applicable_fluid_therapy || 0,
          fluid_therapy: data.fluid_therapy?.length
            ? data.fluid_therapy
            : [
                { name: 'Ns', is_active: 0, value: '' },
                { name: 'Rl', is_active: 0, value: '' },
                { name: 'd545', is_active: 0, value: '' },
                { name: 'other', is_active: 0, value: '' },
              ],
          physical_recomondation: data.physical_recomondation || '',
          transfer_summary: data.transfer_summary || '',
          vital_sign_during_transfer: {
            bp: data.vital_sign_during_transfer?.bp || '',
            p: data.vital_sign_during_transfer?.p || '',
            rr: data.vital_sign_during_transfer?.rr || '',
            spo2: data.vital_sign_during_transfer?.spo2 || '',
            temp: data.vital_sign_during_transfer?.temp || '',
          },
          recieved_party_signatures: {
            received_dr_name: data.recieved_party_signatures?.received_dr_name || '',
            dr_signature: data.recieved_party_signatures?.dr_signature || '',
            received_nurse_name: data.recieved_party_signatures?.received_nurse_name || '',
            nurse_signature: data.recieved_party_signatures?.nurse_signature || '',
            received_paramedic_name:
              data.recieved_party_signatures?.received_paramedic_name || (sessionStorage.getItem('Role_Name') === 'EMS Team' ? sessionStorage.getItem('User_Name') || '' : ''),
            paramedic_signature: data.recieved_party_signatures?.paramedic_signature || '',
            date: data.recieved_party_signatures?.date || new Date().toISOString().split('T')[0],
            time: data.recieved_party_signatures?.time || new Date().toTimeString().split(' ')[0],
          },
          infusion_pump: data.infusion_pump || '',
        }));
        setVitalSignCount(data.vital_signs?.length || 1);
        if ((RoleName === 'Hospital Representative (Nurse)' || RoleName === 'Nurse') && data.clinic_no && data.request_details) {
          setIsFormEditable(false);
        } else {
          setIsFormEditable(true);
        }
      } catch (error) {
        console.error('Error fetching transfer case:', error.response?.data || error.message);
      }
    };

    if (CaseIdTransferCase) fetchTransferCase();
  }, [CaseIdTransferCase, userId, RoleName]);

  // Handle input changes for form fields
  const handleInputChange = (e, parentKey = null, key = null, index = null) => {
    if (!isEditable(parentKey || key)) return;
    const value = e.target.type === 'checkbox' ? (e.target.checked ? 1 : 0) : e.target.value;

    if (parentKey === 'recieved_party_signatures') {
      setFormData((prev) => ({
        ...prev,
        recieved_party_signatures: { ...prev.recieved_party_signatures, [key]: value },
      }));
    } else if (parentKey === 'vital_signs' && index !== null) {
      setFormData((prev) => ({
        ...prev,
        vital_signs: prev.vital_signs.map((vital, i) =>
          i === index ? { ...vital, [key]: value } : vital
        ),
      }));
    } else if (parentKey === 'medication' && index !== null) {
      setFormData((prev) => ({
        ...prev,
        medication: prev.medication.map((med, i) =>
          i === index ? { ...med, [key]: value } : med
        ),
      }));
    } else if (parentKey === 'medication_during_transfer' && index !== null) {
      setFormData((prev) => ({
        ...prev,
        medication_during_transfer: prev.medication_during_transfer.map((med, i) =>
          i === index ? { ...med, [key]: value } : med
        ),
      }));
    } else if (parentKey === 'fluid_therapy' && index !== null) {
      setFormData((prev) => ({
        ...prev,
        fluid_therapy: prev.fluid_therapy.map((fluid, i) =>
          i === index ? { ...fluid, [key]: value } : fluid
        ),
      }));
    } else if (parentKey === 'past_medical_history_pmhx' && key) {
      setFormData((prev) => {
        const updatedPmhx = { ...prev.past_medical_history_pmhx, [key]: value };
        if (key === 'None' && value === 1) {
          updatedPmhx.Cardiac = 0;
          updatedPmhx.Stroke = 0;
          updatedPmhx.Asthma = 0;
          updatedPmhx.Allergy = 0;
          updatedPmhx.HTN = 0;
          updatedPmhx.COPD = 0;
          updatedPmhx.DM = 0;
          updatedPmhx.Other = 0;
          updatedPmhx.Other_value = '';
        } else if (key !== 'Other_value' && key !== 'None' && value === 1) {
          updatedPmhx.None = 0;
        }
        return { ...prev, past_medical_history_pmhx: updatedPmhx };
      });
    } else if (parentKey) {
      setFormData((prev) => ({ ...prev, [parentKey]: { ...prev[parentKey], [key]: value } }));
    } else {
      setFormData((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleMedicationChange = (index, key, value) => {
    if (!isEditable('medication')) return;
    setFormData((prev) => ({
      ...prev,
      medication: prev.medication.map((med, i) =>
        i === index ? { ...med, [key]: value } : med
      ),
    }));
  };

  const addMedicationRow = () => {
    if (!isEditable('medication')) return;
    setFormData((prev) => ({
      ...prev,
      medication: [...prev.medication, { medication_name: '', dose: '', route: '', time: '' }],
    }));
  };

  const removeMedicationRow = (index) => {
    if (!isEditable('medication')) return;
    setFormData((prev) => ({
      ...prev,
      medication: prev.medication.filter((_, i) => i !== index),
    }));
  };

  const handleMedicationDuringTransferChange = (index, key, value) => {
    if (!isEditable('medication_during_transfer')) return;
    setFormData((prev) => ({
      ...prev,
      medication_during_transfer: prev.medication_during_transfer.map((med, i) =>
        i === index ? { ...med, [key]: value } : med
      ),
    }));
  };

  const removeMedicationDuringTransferRow = (index) => {
    if (!isEditable('medication_during_transfer')) return;
    setFormData((prev) => ({
      ...prev,
      medication_during_transfer: prev.medication_during_transfer.filter((_, i) => i !== index),
    }));
  };

  const addMedicationDuringTransferRow = () => {
    if (!isEditable('medication_during_transfer')) return;
    setFormData((prev) => ({
      ...prev,
      medication_during_transfer: [...prev.medication_during_transfer, { medication_name: '', dose: '' }],
    }));
  };

  const openSignatureModal = (field) => {
    if (!isEditable(field.includes('recieved_party_signatures') ? 'recieved_party_signatures' : 'transferring_physician')) return;
    setCurrentSignatureField(field);
    setSignatureModalType(true);
  };

  const handleSignatureSave = async (signature) => {
    let base64Signature = signature;
    if (signature instanceof File || signature instanceof Blob) {
      base64Signature = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(signature);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    }

    if (currentSignatureField) {
      setFormData((prev) => {
        const updated = { ...prev };
        if (currentSignatureField.includes('recieved_party_signatures')) {
          const [, key] = currentSignatureField.split('.');
          updated.recieved_party_signatures = {
            ...prev.recieved_party_signatures,
            [key]: base64Signature || ''
          };
        } else {
          updated[currentSignatureField] = base64Signature || '';
        }
        return updated;
      });
    }
    setSignatureModalType(null);
    setCurrentSignatureField(null);
  };

  const handleDeleteSignature = (field) => {
    if (!isEditable(field.includes('recieved_party_signatures') ? 'recieved_party_signatures' : 'transferring_physician')) return;
    setFormData((prev) => {
      const updated = { ...prev };
      if (field.includes('recieved_party_signatures')) {
        const [, key] = field.split('.');
        updated.recieved_party_signatures = { ...prev.recieved_party_signatures, [key]: '' };
      } else {
        updated[field] = '';
      }
      return updated;
    });
  };

  const handleReasonForTransferChange = (e, reason) => {
    if (!isEditable('reason_for_transfer')) return;
    const value = e.target.checked ? 1 : 0;

    setFormData((prev) => {
      const currentReasons = prev.reason_for_transfer.length > 0
        ? prev.reason_for_transfer[0]
        : { Consultation: 0, Discharge: 0, SpecificManagement: 0, transferofcare: 0 };
      
      const updatedReasons = [{
        ...currentReasons,
        [reason]: value,
      }];
      
      return { ...prev, reason_for_transfer: updatedReasons };
    });
  };

  const handleVitalSignsSave = (newVital) => {
    if (!isEditable('vital_signs')) return;
    if (vitalSignCount >= 4) {
      alert('Cannot add more than 4 vital sign entries');
      return;
    }
    const timestamp = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    setFormData((prev) => ({
      ...prev,
      vital_signs: [...prev.vital_signs, { ...newVital, time: timestamp }],
    }));
    setVitalSignCount((prev) => prev + 1);
    setVitalModalOpen(false);
  };

  const addVitalSignRow = () => {
    if (!isEditable('vital_signs')) return;
    if (vitalSignCount >= 4) {
      alert('Cannot add more than 4 vital sign entries');
      return;
    }
    const timestamp = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    setFormData((prev) => ({
      ...prev,
      vital_signs: [...prev.vital_signs, { BP: '', P: '', RR: '', SPO2: '', Temp: '', time: timestamp }],
    }));
    setVitalSignCount((prev) => prev + 1);
  };

  const removeVitalSignRow = (index, keys) => {
    if (!isEditable('vital_signs')) return;
    if (index === 0 && keys.every((key) => defaultVitalSigns.includes(key))) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      vital_signs: prev.vital_signs.filter((_, i) => i !== index),
    }));
    setVitalSignCount((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (!isEditable('any')) {
      alert('You do not have permission to save this form');
      console.error('Permission error: User does not have permission to save the form', { RoleName });
      return;
    }

    if (!CaseIdTransferCase || !userId) {
      alert('Error: Missing case ID or user ID');
      console.error('Submission error: Missing case ID or user ID', { CaseIdTransferCase, userId });
      return;
    }

    const validVitalSigns = formData.vital_signs.filter(
      (vital) => Object.keys(vital).some((key) => key !== 'time' && vital[key])
    );

    const validMedication = formData.medication.filter(
      (med) => med.medication_name || med.dose || med.route || med.time
    );

    const validMedicationDuringTransfer = formData.medication_during_transfer.filter(
      (med) => med.medication_name || med.dose
    );

    if (requiresValidation()) {
     if (!formData.monitoring_type) return alert('Monitoring type is required');
      
      if (formData.monitoring_type === 'Q' && !formData.monitoring_interval) {
        alert('Monitoring interval is required when Q is selected');
        return;
      }
      if (formData.monitoring_type === 'Q' && !formData.monitoring_unit) {
        alert('Monitoring unit (Minutes or Hours) is required when Q is selected');
        return;
      }
      if (validVitalSigns.length === 0) {
        alert('At least one valid Vital Signs entry is required');
        return;
      }
      for (let i = 0; i < validVitalSigns.length; i++) {
        const vital = validVitalSigns[i];
        for (const key of Object.keys(vital)) {
          if (key !== 'time' && !defaultVitalSigns.includes(key) && !vital[key]) {
            alert(`Vital sign ${key} is required for entry ${i + 1}`);
            return;
          }
          if (i > 0 && !defaultVitalSigns.includes(key) && !vital.time) {
            alert(`Time is required for additional vital sign entry ${i + 1}`);
            return;
          }
        }
      }
      const reason = formData.reason_for_transfer?.[0] || {};
      if (!reason.Consultation && !reason.Discharge && !reason.SpecificManagement && !reason.transferofcare) {
        alert('At least one Reason for Transfer must be selected');
        return;
      }
      if (formData.patient_status === null || formData.patient_status === undefined) {
        alert('Patient Status (Critical or Not Critical) is required');
        return;
      }
      const resp = formData.respiratory_support_type;
      if (!resp.roomair && !resp.incubator && !resp.ventilated && !resp.o2therapy && !resp['l/m']) {
        alert('At least one Respiratory Support option must be selected');
        return;
      }
      const urgency = formData.urgency_of_transfer;
      if (!urgency.emergent && !urgency.urgent && !urgency.semiurgent && !urgency.nonurgent) {
        alert('Urgency of Transfer is required');
        return;
      }
      for (let i = 0; i < validMedication.length; i++) {
        const med = validMedication[i];
        if (med.medication_name && (!med.dose || !med.route || !med.time)) {
          alert(`Dose, Route, and Time are required for medication ${med.medication_name} (entry ${i + 1})`);
          return;
        }
      }
      if (!formData.diagnosis) {
        alert('Diagnosis is required');
        return;
      }
      const pmh = formData.past_medical_history_pmhx;
      if (!pmh.Cardiac && !pmh.Stroke && !pmh.Asthma && !pmh.Allergy && !pmh.HTN && !pmh.COPD && !pmh.DM && !pmh.None && !pmh.Other) {
        alert('At least one Past Medical History option must be selected');
        return;
      }
      if (pmh.Other && !pmh.Other_value) {
        alert('Other value is required when Other is selected in Past Medical History');
        return;
      }
      if (formData.infectious_disease === 1) {
        const prec = formData.precautions;
        if (!prec.Airborne && !prec.Droplet && !prec.Contact && !prec.Bloodborne && !prec.Other) {
          alert('At least one Precaution must be selected when Infectious Disease is Yes');
          return;
        }
        if (prec.Other && !prec.Other_value) {
          alert('Other value is required when Other is selected in Precautions');
          return;
        }
      }
      if (formData.is_medication_during_transfer) {
        if (validMedicationDuringTransfer.length === 0) {
          alert('At least one Medication During Transfer with Name and Dose is required when checkbox is selected');
          return;
        }
      }
      if (formData.is_applicable_ventilator_setting) {
        for (const key of ['tv', 'peep', 'o2', 'rate']) {
          if (!formData.ventilator_setting[key]) {
            alert(`Ventilator setting ${key.toUpperCase()} is required when Ventilator is selected`);
            return;
          }
        }
      }
      if (formData.is_applicable_fluid_therapy) {
        const activeFluids = formData.fluid_therapy.filter((f) => f.is_active && f.value);
        if (!activeFluids.length) {
          alert('At least one Fluid Therapy option with a value is required when Fluid Therapy is selected');
          return;
        }
        const otherFluid = formData.fluid_therapy.find((f) => f.name === 'other');
        if (otherFluid.is_active && !otherFluid.value) {
          alert('Other value is required when Other is selected in Fluid Therapy');
          return;
        }
      }
      if (!formData.transfer_summary) {
        alert('Transfer Summary is required');
        return;
      }
      for (const key of ['bp', 'p', 'rr', 'spo2', 'temp']) {
        if (!formData.vital_sign_during_transfer[key]) {
          alert(`Vital Sign During Transfer ${key.toUpperCase()} is required`);
          return;
        }
      }
    }

    const payload = {
      case_id: CaseIdTransferCase,
      user_id: userId,
      filled_by: RoleName || '',
      transport_mode: formData.transport_mode,
      monitoring_type: formData.monitoring_type,
      monitoring_interval: formData.monitoring_interval,
      monitoring_unit: formData.monitoring_unit,
      transferring_physician: formData.transferring_physician,
      transferring_signature: formData.transferring_signature,
      accepting_physician: formData.accepting_physician,
      contact_no: formData.contact_no,
      vital_signs: validVitalSigns,
      reason_for_transfer: formData.reason_for_transfer,
      patient_status: formData.patient_status,
      respiratory_support_type: formData.respiratory_support_type,
      urgency_of_transfer: formData.urgency_of_transfer,
      medication: validMedication,
      diagnosis: formData.diagnosis,
      clinical_summary: formData.clinical_summary,
      past_medical_history_pmhx: formData.past_medical_history_pmhx,
      infectious_disease: formData.infectious_disease,
      precautions: formData.precautions,
      is_medication_during_transfer: formData.is_medication_during_transfer,
      medication_during_transfer: validMedicationDuringTransfer,
      patient_requirement_during_transfer: formData.patient_requirement_during_transfer,
      is_applicable_ventilator_setting: formData.is_applicable_ventilator_setting,
      ventilator_setting: formData.ventilator_setting,
      is_applicable_fluid_therapy: formData.is_applicable_fluid_therapy,
      fluid_therapy: formData.fluid_therapy,
      physical_recomondation: formData.physical_recomondation,
      transfer_summary: formData.transfer_summary,
      vital_sign_during_transfer: formData.vital_sign_during_transfer,
      recieved_party_signatures: {
        received_dr_name: formData.recieved_party_signatures.received_dr_name,
        dr_signature: formData.recieved_party_signatures.dr_signature || '',
        received_nurse_name: formData.recieved_party_signatures.received_nurse_name,
        nurse_signature: formData.recieved_party_signatures.nurse_signature || '',
        received_paramedic_name: formData.recieved_party_signatures.received_paramedic_name,
        paramedic_signature: formData.recieved_party_signatures.paramedic_signature || '',
        date: formData.recieved_party_signatures.date,
        time: formData.recieved_party_signatures.time,
      },
      infusion_pump: formData.infusion_pump,
    };

    setIsLoading(true);
    try {
      const response = await axiosInstance.post('/ems/transfer-case-patient-situation', payload, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
        },
      });
      if (response.data.status === 'success') {
        alert('Data saved successfully');
        console.log('Data saved successfully', { response: response.data });
        handleClose();
        if (typeof onSave === 'function') {
          onSave(); // Call onSave only if it's a function
        } else {
          console.warn('onSave is not a function, skipping callback');
        }
      } else {
        alert(`Failed to save data: ${response.data.message || 'Unexpected response'}`);
        console.error('Failed to save data', { response: response.data });
      }
    } catch (error) {
      alert(`Failed to save data: ${error.response?.data?.message || error.message}`);
      console.error('Error submitting form', { error: error.response?.data || error.message, payload });
    } finally {
      setIsLoading(false);
    }
  };

  const renderVitalSigns = () => {
    return formData.vital_signs.map((vital, index) => (
      <div key={index} className="vital-signs-row d-flex flex-wrap align-items-end mb-3">
        {Object.keys(vital).map((key) => (
          <InputField
            key={key}
            label={key === 'time' ? 'Time' : key.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            value={vital[key] || ''}
            onChange={(e) => handleInputChange(e, 'vital_signs', key, index)}
            readOnly={key === 'time' || !isEditable('vital_signs')}
            required={
              requiresValidation() &&
              isEditable('vital_signs') &&
              (defaultVitalSigns.includes(key) || (index > 0 && key !== 'time'))
            }
            className="col-two-half mb-2"
          />
        ))}
        {isEditable('vital_signs') && (index !== 0 || !Object.keys(vital).every((key) => defaultVitalSigns.includes(key))) && (
          <div className="col-two-half mb-2">
            <a
              className="delete-vital"
              onClick={(e) => {
                e.preventDefault();
                removeVitalSignRow(index, Object.keys(vital));
              }}
            >
              <i className="ti ti-trash text-danger fs-5"></i>
            </a>
          </div>
        )}
      </div>
    ));
  };

  // Show loading while checking edit availability
  if (editCheckLoading) {
    return (
      <div className={`modal fade patient-new-case ${isOpen ? 'show d-block' : ''}`} id="bs-fullscreen-transfer" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-body d-flex justify-content-center align-items-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="ms-2">Checking edit permissions...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          .modal-fullscreen {
            width: 80% !important;
            height: auto !important;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            margin: auto;
          }
          .col-two-half {
            width: 20%;
            padding: 0 10px;
          }
          @media(max-width: 1299px) {
            .form-section__title { font-size: 14px; }
            .patient-new-case label { font-size: 12px; }
            .patient-new-case .label { font-size: 12px; }
            .patient-new-case .modal-fullscreen { width: calc(100% - 40px) !important; }
          }
          @media(max-width: 767px) {
            .col-two-half { width: 33.33%; }
          }
          @media(max-width: 575px) {
            .col-two-half { width: 50%; }
          }
          a { cursor: pointer; }
          .delete-signature, .delete-medication, .delete-vital {
            cursor: ${isEditable('any') ? 'pointer' : 'not-allowed'};
            color: ${isEditable('any') ? 'red' : '#6c757d'};
            margin-left: 10px;
          }
          .signature-image {
            max-width: 100px;
            max-height: 50px;
            margin-top: 10px;
            border: 1px solid #ccc;
            cursor: ${isEditable('any') ? 'pointer' : 'not-allowed'};
          }
          .signature-button {
            cursor: ${isEditable('recieved_party_signatures') ? 'pointer' : 'not-allowed'};
            color: ${isEditable('recieved_party_signatures') ? '#007bff' : '#6c757d'};
            text-decoration: ${isEditable('recieved_party_signatures') ? 'underline' : 'none'};
            margin-right: 10px;
          }
          .signature-container {
            display: flex;
            align-items: center;
          }
          .form-control[readonly], .form-check-input[readonly] {
            background-color: #e9ecef;
            cursor: not-allowed;
          }
          .monitoring-interval {
            width: 100px;
            margin-left: 10px;
            margin-right: 10px;
          }
          .col-one-forth { width: 40%; }
          .col-one-forth .form-control { width: 100px; }
          .form-check-label {text-transform: capitalize;}
        `}
      </style>

      <div className={`modal fade patient-new-case ${isOpen ? 'show d-block' : ''}`} id="bs-fullscreen-transfer" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title h4">Patient Situation - Transfer Case</h5>
                <button type="button" className="btn-close" onClick={handleClose}></button>
              </div>
              <div className="modal-body">
                {/* <Section title="Transferring Details (FROM)">
                  <div className="row">
                    <InputField
                      label="Clinic No."
                      value={formData.clinic_no}
                      onChange={(e) => handleInputChange(e, null, 'clinic_no')}
                      readOnly={!isEditable('clinic_no')}
                      required={requiresValidation() && (RoleName === 'Hospital Representative (Nurse)' || RoleName === 'Nurse')}
                      className="col-md-6 mb-2"
                    />
                    <InputField
                      label="Request Details"
                      value={formData.request_details}
                      onChange={(e) => handleInputChange(e, null, 'request_details')}
                      readOnly={!isEditable('request_details')}
                      required={requiresValidation() && (RoleName === 'Hospital Representative (Nurse)' || RoleName === 'Nurse')}
                      type="textarea"
                      className="col-md-6 mb-2"
                    />
                    <InputField
                      label="Nurse Name"
                      value={formData.nurse_name}
                      onChange={(e) => handleInputChange(e, null, 'nurse_name')}
                      readOnly={!isEditable('nurse_name') || RoleName === 'Hospital Representative (Nurse)'}
                      required={requiresValidation() && (RoleName === 'Hospital Representative (Nurse)' || RoleName === 'Nurse')}
                      className="col-md-6 mb-2"
                    />
                  </div>
                </Section> */}

                {/* <Section title="Escort Team (TO)">
                  <div className="row">
                    {['escort_physician', 'escort_nurse', 'escort_paramedic', 'other'].map((key) => (
                      <InputField
                        key={key}
                        label={key.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        value={formData.escort_team[key]}
                        onChange={(e) => handleInputChange(e, 'escort_team', key)}
                        readOnly={!isEditable('escort_team')}
                        className="col-two-half mb-2"
                      />
                    ))}
                  </div>
                </Section> */}

                <Section title="Mode of Transport">
                  <div className="row">
                    {['ambulance', 'medivac'].map((mode) => (
                      <CheckboxField
                        key={mode}
                        label={mode.charAt(0).toUpperCase() + mode.slice(1)}
                        checked={formData.transport_mode[mode] === 1}
                        onChange={(e) => handleInputChange(e, 'transport_mode', mode)}
                        disabled={!isEditable('transport_mode')}
                        className="col-two-half mb-2"
                      />
                    ))}
                  </div>
                </Section>

                <Section title="Monitoring">
                  <div className="row">
                    <RadioField
                      label="Continuous"
                      name="monitoringType"
                      checked={formData.monitoring_type === 'Continuous'}
                      onChange={() =>
                        setFormData((prev) => ({
                          ...prev,
                          monitoring_type: 'Continuous',
                          monitoring_interval: '',
                          monitoring_unit: '',
                        }))
                      }
                      disabled={!isEditable('monitoring')}
                      className="col-two-half mb-2"
                    />
                    <div className="col-two-half mb-2 d-flex align-items-center">
                      <RadioField
                        label="Q"
                        name="monitoringType"
                        checked={formData.monitoring_type === 'Q'}
                        onChange={() => setFormData((prev) => ({ ...prev, monitoring_type: 'Q' }))}
                        disabled={!isEditable('monitoring')}
                      />
                      {formData.monitoring_type === 'Q' && (
                        <>
                          <input
                            type="number"
                            className="form-control monitoring-interval"
                            value={formData.monitoring_interval}
                            onChange={(e) => handleInputChange(e, null, 'monitoring_interval')}
                            readOnly={!isEditable('monitoring')}
                            required={requiresValidation() && formData.monitoring_type === 'Q'}
                            min="1"
                          />
                          <div className="d-flex">
                            <RadioField
                              label="Minutes"
                              name="monitoringUnit"
                              checked={formData.monitoring_unit === 'min'}
                              onChange={() => setFormData((prev) => ({ ...prev, monitoring_unit: 'min' }))}
                              disabled={!isEditable('monitoring')}
                              className="me-3"
                            />
                            <RadioField
                              label="Hours"
                              name="monitoringUnit"
                              checked={formData.monitoring_unit === 'hr'}
                              onChange={() => setFormData((prev) => ({ ...prev, monitoring_unit: 'hr' }))}
                              disabled={!isEditable('monitoring')}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Section>

                <Section title="Transferring & Accepting Physicians">
                  <div className="row">
                    <InputField
                      label="Transferring Physician"
                      value={formData.transferring_physician}
                      onChange={(e) => handleInputChange(e, null, 'transferring_physician')}
                      readOnly={!isEditable('transferring_physician')}
                      className="col-two-half mb-2"
                    />
                    <div className="col-two-half mb-2 signature-container align-items-end">
                      {formData.transferring_signature ? (
                        <img
                          src={formData.transferring_signature}
                          alt="Signature"
                          className="signature-image"
                          onClick={() => openSignatureModal('transferring_signature')}
                        />
                      ) : (
                        <a
                          type="button"
                          className="text-primary signature-button"
                          onClick={() => openSignatureModal('transferring_signature')}
                          disabled={!isEditable('transferring_physician')}
                        >
                          <i className="ti ti-circle-plus"></i> Add Signature
                        </a>
                      )}
                      {isEditable('transferring_physician') && formData.transferring_signature && (
                        <a
                          className="delete-signature"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDeleteSignature('transferring_signature');
                          }}
                        >
                          <i className="ti ti-trash text-danger fs-5"></i>
                        </a>
                      )}
                    </div>
                    <InputField
                      label="Accepting Physician"
                      value={formData.accepting_physician}
                      onChange={(e) => handleInputChange(e, null, 'accepting_physician')}
                      readOnly={!isEditable('accepting_physician')}
                      className="col-two-half mb-2"
                    />
                    <InputField
                      label="Contact No."
                      value={formData.contact_no}
                      onChange={(e) => handleInputChange(e, null, 'contact_no')}
                      readOnly={!isEditable('contact_no')}
                      className="col-two-half mb-2"
                    />
                  </div>
                </Section>

                <Section title="Vital Signs (Current/Baseline for Transfer)">
                  <div className="row">
                    <div className="col-12 mb-2">
                      {renderVitalSigns()}
                      {isEditable('vital_signs') && vitalSignCount < 4 && (
                        <div className="col-two-half mb-2 d-flex align-items-center">
                          <a
                            className="add-more-vital-signs ms-3"
                            onClick={(e) => {
                              e.preventDefault();
                              addVitalSignRow();
                            }}
                            style={{
                              cursor: 'pointer',
                              color: '#007bff',
                              textDecoration: 'underline',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <i className="ti ti-circle-plus text-primary"></i> Add Vital Sign
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </Section>

                <Section title="Reason for Transfer">
                  <div className="row">
                    {(formData.reason_for_transfer?.[0] ? Object.keys(formData.reason_for_transfer[0]) : []).map(
                      (reason) => (
                        <CheckboxField
                          key={reason}
                          label={reason.replace(/([A-Z])/g, ' $1').trim()}
                          checked={formData.reason_for_transfer[0][reason] === 1}
                          onChange={(e) => handleReasonForTransferChange(e, reason)}
                          disabled={!isEditable('reason_for_transfer')}
                          className="col-two-half mb-2"
                        />
                      )
                    )}
                  </div>
                </Section>

                <Section title="Patient Status">
                  <div className='row'>
                    <RadioField
                      label="Critical"
                      name="patientStatus"
                      checked={formData.patient_status === 1}
                      onChange={() => setFormData((prev) => ({ ...prev, patient_status: 1 }))}
                      disabled={!isEditable('patient_status')}
                      className="col-two-half mb-2"
                    />
                    <RadioField
                      label="Not Critical"
                      name="patientStatus"
                      checked={formData.patient_status === 0}
                      onChange={() => setFormData((prev) => ({ ...prev, patient_status: 0 }))}
                      disabled={!isEditable('patient_status')}
                      className="col-two-half mb-2"
                    />
                  </div>
                </Section>

                <Section title="Patient On (Respiratory Support)">
                  <div className="row">
                    {['roomair', 'incubator', 'ventilated', 'o2therapy', 'l/m'].map((type) => (
                      <CheckboxField
                        key={type}
                        label={type === 'l/m' ? 'L/m' : type.replace(/([A-Z])/g, ' $1').trim()}
                        checked={formData.respiratory_support_type[type] === 1}
                        onChange={(e) => handleInputChange(e, 'respiratory_support_type', type)}
                        disabled={!isEditable('respiratory_support')}
                        className="col-two-half mb-2"
                      />
                    ))}
                  </div>
                </Section>

                <Section title="Urgency of Transfer">
                  <div className="row">
                    {['emergent', 'urgent', 'semiurgent', 'nonurgent'].map((urgency) => (
                      <RadioField
                        key={urgency}
                        label={urgency.replace(/([A-Z])/g, ' $1').trim()}
                        name="urgency_of_transfer"
                        checked={formData.urgency_of_transfer[urgency] === 1}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            urgency_of_transfer: {
                              emergent: urgency === 'emergent' ? 1 : 0,
                              urgent: urgency === 'urgent' ? 1 : 0,
                              semiurgent: urgency === 'semiurgent' ? 1 : 0,
                              nonurgent: urgency === 'nonurgent' ? 1 : 0,
                            },
                          }))
                        }
                        disabled={!isEditable('urgency_of_transfer')}
                        className="col-two-half mb-2"
                      />
                    ))}
                  </div>
                </Section>

                <Section title="Medication">
                  {formData.medication.map((med, index) => (
                    <div className="row align-items-end mb-2" key={index}>
                      {['medication_name', 'dose', 'route', 'time'].map((key) => (
                        <InputField
                          key={key}
                          label={key.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          value={med[key]}
                          onChange={(e) => handleMedicationChange(index, key, e.target.value)}
                          readOnly={!isEditable('medication')}
                          required={requiresValidation() && isEditable('medication') && !!med.medication_name}
                          className="col-two-half mb-2"
                        />
                      ))}
                      {isEditable('medication') && (
                        <div className="col-two-half mb-2">
                          <a
                            className="delete-medication"
                            onClick={(e) => {
                              e.preventDefault();
                              removeMedicationRow(index);
                            }}
                          >
                            <i className="ti ti-trash text-danger fs-5"></i>
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                  {isEditable('medication') && (
                    <div className="row">
                      <div className="col-12 mb-2">
                        <a
                          className="add-more-medication"
                          onClick={(e) => {
                            e.preventDefault();
                            addMedicationRow();
                          }}
                        >
                          <i className="ti ti-circle-plus text-primary"></i> Add More
                        </a>
                      </div>
                    </div>
                  )}
                </Section>

                <Section title="Diagnosis">
                  <textarea
                    className="form-control"
                    rows="5"
                    value={formData.diagnosis}
                    onChange={(e) => handleInputChange(e, null, 'diagnosis')}
                    readOnly={!isEditable('diagnosis')}
                    required={requiresValidation() && isEditable('diagnosis')}
                  />
                </Section>

                <Section title="Clinical Summary">
                  <textarea
                    className="form-control"
                    rows="5"
                    value={formData.clinical_summary}
                    onChange={(e) => handleInputChange(e, null, 'clinical_summary')}
                    readOnly={!isEditable('clinical_summary')}
                  />
                </Section>

                <Section title="Past Medical History (P.M. Hx)">
                  <div className="row">
                    {['Cardiac', 'Stroke', 'Asthma', 'Allergy', 'HTN', 'COPD', 'DM', 'None'].map((condition) => (
                      <CheckboxField
                        key={condition}
                        label={condition}
                        checked={formData.past_medical_history_pmhx[condition] === 1}
                        onChange={(e) => handleInputChange(e, 'past_medical_history_pmhx', condition)}
                        disabled={!isEditable('past_medical_history_pmhx')}
                        className="col-two-half mb-2"
                      />
                    ))}
                    <div className="col-4 mb-2">
                      <CheckboxField
                        label="Other"
                        checked={formData.past_medical_history_pmhx.Other === 1}
                        onChange={(e) => handleInputChange(e, 'past_medical_history_pmhx', 'Other')}
                        disabled={!isEditable('past_medical_history_pmhx')}
                      />
                      <textarea
                        className="form-control"
                        rows="4"
                        value={formData.past_medical_history_pmhx.Other_value}
                        onChange={(e) => handleInputChange(e, 'past_medical_history_pmhx', 'Other_value')}
                        readOnly={!isEditable('past_medical_history_pmhx')}
                        required={requiresValidation() && isEditable('past_medical_history_pmhx') && formData.past_medical_history_pmhx.Other === 1}
                      />
                    </div>
                  </div>
                </Section>

                <Section title="Infectious Disease & Precautions">
                  <div className="row">
                    <label>Infectious Disease</label>
                    {['Yes', 'No'].map((option) => (
                      <RadioField
                        key={option}
                        label={option}
                        name="infectiousDisease"
                        checked={formData.infectious_disease === (option === 'Yes' ? 1 : 0)}
                        onChange={() => setFormData((prev) => ({ ...prev, infectious_disease: option === 'Yes' ? 1 : 0 }))}
                        disabled={!isEditable('infectious_disease')}
                        className="col-two-half mb-2"
                      />
                    ))}
                  </div>
                  <div className="row mt-3">
                    <label>Precautions</label>
                    {['Airborne', 'Droplet', 'Contact', 'Bloodborne', 'Other'].map((precaution) => (
                      <CheckboxField
                        key={precaution}
                        label={precaution}
                        checked={formData.precautions[precaution] === 1}
                        onChange={(e) => handleInputChange(e, 'precautions', precaution)}
                        disabled={!isEditable('precautions')}
                        className="col-two-half mb-2"
                      />
                    ))}
                    <div className="col-12">
                      <textarea
                        className="form-control mt-2"
                        rows="4"
                        value={formData.precautions.Other_value}
                        onChange={(e) => handleInputChange(e, 'precautions', 'Other_value')}
                        readOnly={!isEditable('precautions')}
                        required={requiresValidation() && isEditable('precautions') && formData.precautions.Other === 1}
                      />
                    </div>
                  </div>
                </Section>

                <Section title="Medication During Transfer">
                  <CheckboxField
                    label="Medication During Transfer"
                    checked={formData.is_medication_during_transfer === 1}
                    onChange={(e) => handleInputChange(e, null, 'is_medication_during_transfer')}
                    disabled={!isEditable('medication_during_transfer')}
                    className=" mb-2"
                  />
                  {formData.medication_during_transfer.map((med, index) => (
                    <div className="row align-items-end mb-2" key={index}>
                      <InputField
                        label="Medication Name"
                        value={med.medication_name}
                        onChange={(e) => handleInputChange(e, 'medication_during_transfer', 'medication_name', index)}
                        readOnly={!isEditable('medication_during_transfer')}
                        required={requiresValidation() && isEditable('medication_during_transfer') && formData.is_medication_during_transfer}
                        className="col-5 mb-2"
                      />
                      <InputField
                        label="Dose"
                        value={med.dose}
                        onChange={(e) => handleInputChange(e, 'medication_during_transfer', 'dose', index)}
                        readOnly={!isEditable('medication_during_transfer')}
                        required={requiresValidation() && isEditable('medication_during_transfer') && formData.is_medication_during_transfer}
                        className="col-5 mb-2"
                      />
                      {isEditable('medication_during_transfer') && (
                        <div className="col-2 mb-2">
                          <a
                            className="delete-medication"
                            onClick={(e) => {
                              e.preventDefault();
                              removeMedicationDuringTransferRow(index);
                            }}
                          >
                            <i className="ti ti-trash text-danger fs-5"></i>
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                  {isEditable('medication_during_transfer') && (
                    <div className="row">
                      <div className="col-12 mb-2">
                        <a
                          className="add-more-medication"
                          onClick={(e) => {
                            e.preventDefault();
                            addMedicationDuringTransferRow();
                          }}
                        >
                          <i className="ti ti-circle-plus text-primary"></i> Add More
                        </a>
                      </div>
                    </div>
                  )}
                </Section>

                <Section title="Patient Requirement During Transport">
                  <textarea
                    className="form-control"
                    rows="4"
                    value={formData.patient_requirement_during_transfer}
                    onChange={(e) => handleInputChange(e, null, 'patient_requirement_during_transfer')}
                    readOnly={!isEditable('patient_requirement_during_transfer')}
                  />
                </Section>

                <Section title="Ventilator Settings (if applicable)">
                  <div className="row">
                    {['tv', 'peep', 'o2', 'rate'].map((setting) => (
                      <div className="col-two-half d-flex align-items-center mb-2" key={setting}>
                        <label className="me-1">{setting.toUpperCase()}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.ventilator_setting[setting]}
                          onChange={(e) => handleInputChange(e, 'ventilator_setting', setting)}
                          readOnly={!isEditable('ventilator')}
                          required={requiresValidation() && isEditable('ventilator') && formData.is_applicable_ventilator_setting}
                        />
                        <label className="ms-1">{setting === 'tv' ? 'ml' : setting === 'peep' ? 'mbr' : setting === 'o2' ? '%' : '/min'}</label>
                      </div>
                    ))}
                  </div>
                </Section>

                <Section title="Fluid Therapy (if applicable)">
                  <div className="row mb-2">
                    {formData.fluid_therapy.map((fluid, index) => (
                      <div className="col-two-half d-flex align-items-center mb-2" key={fluid.name}>
                        <CheckboxField
                          label={fluid.name === 'd545' ? 'd5%.45' : fluid.name}
                          checked={fluid.is_active === 1}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              fluid_therapy: prev.fluid_therapy.map((f, i) =>
                                i === index ? { ...f, is_active: e.target.checked ? 1 : 0 } : f
                              ),
                            }))
                          }
                          disabled={!isEditable('fluid_therapy')}
                        />
                        <input
                          type="text"
                          className="form-control ms-2"
                          value={fluid.value}
                          onChange={(e) => handleInputChange(e, 'fluid_therapy', 'value', index)}
                          readOnly={!isEditable('fluid_therapy')}
                          required={requiresValidation() && isEditable('fluid_therapy') && fluid.is_active}
                        />
                        {fluid.name !== 'other' && <label className="ms-1">ml/hr</label>}
                      </div>
                    ))}
                  </div>
                </Section>

                <Section title="Special Consideration or Physician Recommendations">
                  <textarea
                    className="form-control"
                    rows="5"
                    value={formData.physical_recomondation}
                    onChange={(e) => handleInputChange(e, null, 'physical_recomondation')}
                    readOnly={!isEditable('physical_recomondation')}
                  />
                </Section>

                <Section title="Transfer Summary">
                  <textarea
                    className="form-control"
                    rows="5"
                    value={formData.transfer_summary}
                    onChange={(e) => handleInputChange(e, null, 'transfer_summary')}
                    readOnly={!isEditable('transfer_summary')}
                    required={requiresValidation() && isEditable('transfer_summary')}
                  />
                </Section>

                <Section title="Vital Signs During Transfer">
                  <div className="row">
                    {['bp', 'p', 'rr', 'spo2', 'temp'].map((vital) => (
                      <InputField
                        key={vital}
                        label={vital.toUpperCase()}
                        value={formData.vital_sign_during_transfer[vital]}
                        onChange={(e) => handleInputChange(e, 'vital_sign_during_transfer', vital)}
                        readOnly={!isEditable('vital_sign_during_transfer')}
                        required={requiresValidation() && isEditable('vital_sign_during_transfer')}
                        className="col-two-half mb-2"
                      />
                    ))}
                  </div>
                </Section>

                <Section title="Receiving Party Signatures">
                  {[
                    { name: 'received_dr_name', signature: 'dr_signature', label: 'Doctor' },
                    { name: 'received_nurse_name', signature: 'nurse_signature', label: 'Nurse' },
                    { name: 'received_paramedic_name', signature: 'paramedic_signature', label: 'Paramedic' },
                  ].map((role) => (
                    <div key={role.name} className="signature-block mb-3">
                      <div className="row mb-2">
                        <InputField
                          label={`${role.label} Name`}
                          value={formData.recieved_party_signatures[role.name]}
                          onChange={(e) => handleInputChange(e, 'recieved_party_signatures', role.name)}
                          readOnly={!isEditable('recieved_party_signatures') || role.name === 'received_paramedic_name'}
                          className="col-md-3 mb-2"
                        />
                        <div className="col-md-4 mb-2 signature-container align-items-end">
                          {formData.recieved_party_signatures[role.signature] ? (
                            <img
                              src={formData.recieved_party_signatures[role.signature]}
                              alt="Signature"
                              className="signature-image"
                              onClick={() => openSignatureModal(`recieved_party_signatures.${role.signature}`)}
                            />
                          ) : (
                            <a
                              type="button"
                              className="text-primary signature-button"
                              onClick={() => openSignatureModal(`recieved_party_signatures.${role.signature}`)}
                              disabled={!isEditable('recieved_party_signatures')}
                            >
                              <i className="ti ti-circle-plus"></i> Add Signature
                            </a>
                          )}
                          {isEditable('recieved_party_signatures') && formData.recieved_party_signatures[role.signature] && (
                            <a
                              className="delete-signature"
                              onClick={(e) => {
                                e.preventDefault();
                                handleDeleteSignature(`recieved_party_signatures.${role.signature}`);
                              }}
                            >
                              <i className="ti ti-trash text-danger fs-5"></i>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="row mb-2">
                    <InputField
                      label="Date"
                      value={formData.recieved_party_signatures.date}
                      onChange={(e) => handleInputChange(e, 'recieved_party_signatures', 'date')}
                      readOnly={true}
                      className="col-md-3 mb-2"
                    />
                    <InputField
                      label="Time"
                      value={formData.recieved_party_signatures.time}
                      onChange={(e) => handleInputChange(e, 'recieved_party_signatures', 'time')}
                      readOnly={true}
                      className="col-md-3 mb-2"
                    />
                  </div>
                </Section>
              </div>
              <div className="modal-footer">
                {isEditable('any') && (
                  <button 
                    type="submit" 
                    className="btn btn-success"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                )}
                <button  
                  type="button" 
                  className="btn bg-danger-subtle text-danger font-medium" 
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>

        <SignatureModal
          isOpen={signatureModalType}
          onClose={() => {
            setSignatureModalType(null);
            setCurrentSignatureField(null);
          }}
          onSave={handleSignatureSave}
          index={currentSignatureField}
          isEditable={isEditable('recieved_party_signatures') || isEditable('transferring_physician')}
        />
        <VitalSignsModal
          isOpen={vitalModalOpen}
          onClose={() => setVitalModalOpen(false)}
          onSave={handleVitalSignsSave}
          isEditable={isEditable('vital_signs')}
          existingVitalSigns={formData.vital_signs.reduce((acc, vital) => ({ ...acc, ...vital }), {})}
          caseId={CaseIdTransferCase || ''}
        />
      </div>
    </>
  );
};

const Section = ({ title, children }) => (
  <div className="row py-3">
    <div className="col-lg-3">
      <h5 className="form-section__title">{title}</h5>
    </div>
    <div className="col-lg-9">{children}</div>
  </div>
);

const InputField = ({ label, value, onChange, readOnly, required, className = 'col-md-6 mb-2', type = 'text' }) => (
  <div className={className}>
    <label>{label}</label>
    {type === 'textarea' ? (
      <textarea className="form-control" rows="4" value={value || ''} onChange={onChange} readOnly={readOnly} required={required} />
    ) : (
      <input type={type} className="form-control" value={value || ''} onChange={onChange} readOnly={readOnly} required={required} />
    )}
  </div>
);

const CheckboxField = ({ label, checked, onChange, disabled, className = 'mb-2' }) => (
  <div className={className}>
    <div className="form-check">
      <input type="checkbox" className="form-check-input" checked={checked} onChange={onChange} disabled={disabled} />
      <label className="form-check-label">{label}</label>
    </div>
  </div>
);

const RadioField = ({ label, name, checked, onChange, disabled, className = 'mb-2' }) => (
  <div className={className}>
    <div className="form-check">
      <input type="radio" name={name} className="form-check-input" checked={checked} onChange={onChange} disabled={disabled} />
      <label className="form-check-label">{label}</label>
    </div>
  </div>
);

PatientSituationtransfercaseModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func, // Made onSave optional
};

export default PatientSituationtransfercaseModal;